import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Card from '../components/Card';
import api from '../services/api';
import Logo from '../components/Logo';
import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './BusinessRequirementGenerator.css';
import pdfToText from "react-pdftotext";
//import { convertMarkdownToDocx, downloadDocx } from "@mohtasham/md-to-docx";
// Helper function to format business requirements for display
const formatBusinessRequirements = (text) => {
  if (!text) return '';
  
  // Define required sections in order
  const requiredSections = [
    { id: "1", title: "Document Control", content: "" },
    { id: "2", title: "Executive Summary", content: "" },
    { id: "3", title: "Background/Context", content: "" },
    { id: "4", title: "Scope", content: "" },
    { id: "5", title: "Business Requirements", content: "" },
    { id: "6", title: "User Requirements", content: "" },
    { id: "7", title: "Business Rules & Constraints", content: "" },
    { id: "8", title: "Functional Requirements", content: "" },
    { id: "9", title: "Assumptions & Dependencies", content: "" },
    { id: "10", title: "Success Criteria", content: "" },
    { id: "11", title: "Glossary/Definitions", content: "" },
    { id: "12", title: "Appendices", content: "" }
  ];
  
  // Clean up any YAML front matter
  let cleanText = text.replace(/^---$\n([\s\S]*?)^---$\n/gm, '');
  
  // Parse the content into sections
  const parseSections = (content) => {
    const sections = [];
    const lines = content.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check if this line is a heading (starts with # or is a clear section title)
      const headingMatch = line.match(/^#+\s*(.+)$/) || 
                          line.match(/^(\d+\.?\s*[A-Z][^:]*):?\s*$/) ||
                          (line.trim() && requiredSections.some(s => 
                            line.toLowerCase().includes(s.title.toLowerCase()) && 
                            line.length < 50
                          ));
      
      if (headingMatch) {
        // Save previous section
        if (currentSection) {
          sections.push({
            ...currentSection,
            content: currentContent.join('\n').trim()
          });
        }
        
        // Start new section
        let title = headingMatch[1] || line.trim();
        title = title.replace(/[:#]+$/, '').trim(); // Remove trailing colons and hashes
        
        // Find matching required section
        const matchedSection = requiredSections.find(s => 
          title.toLowerCase().includes(s.title.toLowerCase()) ||
          s.title.toLowerCase().includes(title.toLowerCase())
        );
        
        currentSection = {
          id: matchedSection ? matchedSection.id : String(sections.length + 1),
          title: matchedSection ? matchedSection.title : title,
          originalTitle: title
        };
        currentContent = [];
      } else if (line.trim()) {
        // Add content to current section
        currentContent.push(line);
      } else if (currentContent.length > 0) {
        // Add blank line only if we have content
        currentContent.push('');
      }
    }
    
    // Save last section
    if (currentSection) {
      sections.push({
        ...currentSection,
        content: currentContent.join('\n').trim()
      });
    }
    
    return sections;
  };
  
  // Parse sections from the input
  const parsedSections = parseSections(cleanText);
  
  // Merge with required sections
  const finalSections = requiredSections.map(reqSection => {
    const foundSection = parsedSections.find(s => s.id === reqSection.id);
    return {
      ...reqSection,
      content: foundSection ? foundSection.content : "No information provided."
    };
  });
  
  // Add any extra sections that don't match required ones
  parsedSections.forEach(section => {
    if (!requiredSections.some(req => req.id === section.id)) {
      finalSections.push(section);
    }
  });
  
  // Format content for each section
  const formatSectionContent = (content) => {
    if (!content || content === "No information provided.") {
      return '<p class="empty-section">No information provided.</p>';
    }
    
    let formatted = content;
    
    // Handle tables with proper formatting
    formatted = formatted.replace(/(\|[^\n]+\|\n)+/g, (match) => {
      const rows = match.trim().split('\n');
      let tableHTML = '<div class="table-container"><table class="doc-table">';
      
      rows.forEach((row, rowIndex) => {
        // Skip separator rows
        if (row.match(/^\|[-|\s]+\|$/)) return;
        
        const cells = row.split('|').filter(cell => cell !== '');
        const isHeader = rowIndex === 0;
        
        tableHTML += '<tr>';
        cells.forEach(cell => {
          const cellContent = cell.trim();
          const isPkFk = cellContent.includes('(PK)') || cellContent.includes('(FK)');
          
          if (isHeader) {
            tableHTML += `<th>${cellContent}</th>`;
          } else {
            if (isPkFk) {
              const formattedCell = cellContent
                .replace(/\(PK\)/g, '<span class="pk-highlight">(PK)</span>')
                .replace(/\(FK\)/g, '<span class="fk-highlight">(FK)</span>');
              tableHTML += `<td>${formattedCell}</td>`;
            } else {
              tableHTML += `<td>${cellContent}</td>`;
            }
          }
        });
        tableHTML += '</tr>';
      });
      
      tableHTML += '</table></div>';
      return tableHTML;
    });
    
    // Handle bullet points and lists
    const lines = formatted.split('\n');
    let inList = false;
    let listItems = [];
    let processedLines = [];
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed.match(/^[-●•]\s/)) {
        if (!inList) {
          inList = true;
          listItems = [];
        }
        const listItemContent = trimmed.substring(1).trim();
        // Handle nested bullet points
        if (listItemContent.includes('\n')) {
          const nestedItems = listItemContent.split('\n').map(item => 
            item.trim().startsWith('-') ? 
              `<li class="nested">${item.substring(1).trim()}</li>` : 
              item
          ).join('');
          listItems.push(`<li>${nestedItems}</li>`);
        } else {
          listItems.push(`<li>${listItemContent}</li>`);
        }
      } else {
        if (inList) {
          processedLines.push(`<ul class="doc-list">${listItems.join('')}</ul>`);
          listItems = [];
          inList = false;
        }
        if (trimmed) {
          // Handle section sub-headings
          if (trimmed.match(/^[A-Z][\w\s-]+:$/)) {
            processedLines.push(`<h4 class="sub-heading">${trimmed}</h4>`);
          } else {
            processedLines.push(`<p>${trimmed}</p>`);
          }
        }
      }
    });
    
    // Handle any remaining list
    if (inList && listItems.length > 0) {
      processedLines.push(`<ul class="doc-list">${listItems.join('')}</ul>`);
    }
    
    if (processedLines.length > 0) {
      formatted = processedLines.join('\n');
    }
    
    // Handle bold text and emphasis
    formatted = formatted
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>');
    
    return formatted;
  };
  
  // Generate final HTML
  let html = '<h1 class="doc-title">Business Requirements Document</h1>';
  
  // Add document control table for section 1
  if (finalSections[0] && finalSections[0].id === "1") {
    html += `<h2 class="section-heading" data-number="1.">Document Control</h2>`;
    html += `
      <div class="table-container">
        <table class="doc-table">
          <tr>
            <th>Version</th>
            <th>Date</th>
            <th>Author</th>
            <th>Description</th>
          </tr>
          <tr>
            <td>1.0</td>
            <td>${new Date().toLocaleDateString()}</td>
            <td>Generated</td>
            <td>Initial Draft</td>
          </tr>
        </table>
      </div>
    `;
  }
  
  // Add all other sections
  finalSections.slice(1).forEach(section => {
    html += `<h2 class="section-heading" data-number="${section.id}.">${section.title}</h2>`;
    html += formatSectionContent(section.content);
  });
  
  return html;
};

const BusinessRequirementGenerator = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [activeTab, setActiveTab] = useState('input');
  
  React.useEffect(() => {
    window.debugWordConversion = debugWordConversion;
    const storedResult = sessionStorage.getItem('businessRequirementResult');
    if (storedResult) {
      setResult(storedResult);
    }
  }, []);
  
  const [stakeholderInputs, setStakeholderInputs] = useState({
    business_context: { question: "What is the business context or domain?", answer: "" },
    primary_goal: { question: "What is the primary goal of this project?", answer: "" },
    key_stakeholders: { question: "Who are the key stakeholders?", answer: "" },
    target_users: { question: "Who are the target users?", answer: "" },
    key_features: { question: "What are the key features needed?", answer: "" },
    data_sources: { question: "What data sources will be used?", answer: "" },
    constraints: { question: "Are there any constraints or limitations?", answer: "" },
    success_criteria: { question: "What are the success criteria?", answer: "" },
    unstructured_data: { question: "Raw uploaded content", answer: "" } // New field
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();
    const fileType = file.type;

    // Handle PDF
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      pdfToText(file)
        .then((text) => {
          setStakeholderInputs(prev => ({
            ...prev,
            unstructured_data: {
              question: "Raw uploaded content",
              answer: text
            }
          }));
        })
        .catch((error) => {
          console.error("Failed to extract text from PDF", error);
          alert("Failed to extract text from PDF file.");
        });
      return;
    }

    // Handle plain text
    if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setStakeholderInputs(prev => ({
          ...prev,
          unstructured_data: {
            question: "Raw uploaded content",
            answer: e.target.result
          }
        }));
      };
      reader.onerror = (e) => {
        console.error("Failed to read text file", e);
        alert("Failed to read text file.");
      };
      reader.readAsText(file);
      return;
    }

    // Handle Word documents (.doc, .docx)
    if (
      fileType === 'application/msword' ||
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx')
    ) {
      // Try to use mammoth if available
      if (window.mammoth) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const arrayBuffer = e.target.result;
            const result = await window.mammoth.extractRawText({ arrayBuffer });
            setStakeholderInputs(prev => ({
              ...prev,
              unstructured_data: {
                question: "Raw uploaded content",
                answer: result.value
              }
            }));
          } catch (err) {
            console.error("Failed to extract text from Word file", err);
            alert("Failed to extract text from Word file.");
          }
        };
        reader.onerror = (e) => {
          console.error("Failed to read Word file", e);
          alert("Failed to read Word file.");
        };
        reader.readAsArrayBuffer(file);
      } else {
        alert("Word file upload requires the mammoth.js library to be loaded on the page.");
      }
      return;
    }

    // Fallback for unsupported types
    alert("Unsupported file type. Please upload a .pdf, .doc, .docx, or .txt file.");
    event.target.value = "";
  };

  const handleFileUpload = (files) => {
    const newFiles = Array.from(files).map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file: file
    }));
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
    
    // Process the first file for content extraction (existing logic)
    if (files.length > 0) {
      const file = files[0];
      const fileName = file.name.toLowerCase();
      const fileType = file.type;

      // Handle PDF
      if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
        pdfToText(file)
          .then((text) => {
            setStakeholderInputs(prev => ({
              ...prev,
              unstructured_data: {
                question: "Raw uploaded content",
                answer: text
              }
            }));
          })
          .catch((error) => {
            console.error("Failed to extract text from PDF", error);
            alert("Failed to extract text from PDF file.");
          });
        return;
      }

      // Handle plain text
      if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setStakeholderInputs(prev => ({
            ...prev,
            unstructured_data: {
              question: "Raw uploaded content",
              answer: e.target.result
            }
          }));
        };
        reader.onerror = (e) => {
          console.error("Failed to read text file", e);
          alert("Failed to read text file.");
        };
        reader.readAsText(file);
        return;
      }

      // Handle Word documents (.doc, .docx)
      if (
        fileType === 'application/msword' ||
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileName.endsWith('.doc') ||
        fileName.endsWith('.docx')
      ) {
        // Try to use mammoth if available
        if (window.mammoth) {
          const reader = new FileReader();
          reader.onload = async (e) => {
            try {
              const arrayBuffer = e.target.result;
              const result = await window.mammoth.extractRawText({ arrayBuffer });
              setStakeholderInputs(prev => ({
                ...prev,
                unstructured_data: {
                  question: "Raw uploaded content",
                  answer: result.value
                }
              }));
            } catch (err) {
              console.error("Failed to extract text from Word file", err);
              alert("Failed to extract text from Word file.");
            }
          };
          reader.onerror = (e) => {
            console.error("Failed to read Word file", e);
            alert("Failed to read Word file.");
          };
          reader.readAsArrayBuffer(file);
        } else {
          alert("Word file upload requires the mammoth.js library to be loaded on the page.");
        }
        return;
      }
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    
    // If this was the first file, clear the unstructured data
    if (index === 0) {
      setStakeholderInputs(prev => ({
        ...prev,
        unstructured_data: {
          question: "Raw uploaded content",
          answer: ""
        }
      }));
    }
  };

  const [uploadedFile, setUploadedFile] = useState(null);
  const handleInputChange = (field, value) => {
    setStakeholderInputs(prev => ({
      ...prev,
      [field]: { ...prev[field], answer: value }
    }));
  };

  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userId = api.getAuthenticatedUserId();
      console.log('Using authenticated user ID for business requirement generation:', userId);
      const response = await api.generateBusinessRequirement(stakeholderInputs, userId);
      
      if (response && response.data) {
        let rawResult = response.data.business_requirement || response.data;
        if (typeof rawResult !== 'string') {
            rawResult = JSON.stringify(rawResult, null, 2);
        }
        const cleanedResult = rawResult.replace(/^---$\n([\s\S]*?)^---$\n/gm, '');
        // Check for error keywords in cleanedResult and replace with friendly message
        setResult(cleanedResult);
        sessionStorage.setItem('businessRequirementResult', cleanedResult);
        sessionStorage.setItem('structuredContent', cleanedResult);
        toast.success("Business requirements generated successfully!");
      }
    } catch (error) {
      console.error("Error generating business requirements:", error);
      setResult("Error generating business requirements. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadStandardInputs = () => {
    setStakeholderInputs({
      business_context: { 
        question: "What is the business context or domain?", 
        answer: "E-commerce retail platform for fashion products" 
      },
      primary_goal: { 
        question: "What is the primary goal of this project?", 
        answer: "Create a database to manage product inventory, customer orders, and sales analytics" 
      },
      key_stakeholders: { 
        question: "Who are the key stakeholders?", 
        answer: "Sales team, inventory managers, marketing department, executive leadership" 
      },
      target_users: { 
        question: "Who are the target users?", 
        answer: "Internal staff (inventory managers, sales representatives) and system integrations with the e-commerce platform" 
      },
      key_features: { 
        question: "What are the key features needed?", 
        answer: "● Product catalog management\n● Inventory tracking\n● Order processing\n● Customer management\n● Sales reporting" 
      },
      data_sources: { 
        question: "What data sources will be used?", 
        answer: "Existing product spreadsheets, customer CRM system, sales transaction logs" 
      },
      constraints: { 
        question: "Are there any constraints or limitations?", 
        answer: "Must integrate with existing e-commerce platform API, support high transaction volume during sales periods" 
      },
      success_criteria: { 
        question: "What are the success criteria?", 
        answer: "● Accurate inventory tracking\n● Reduced order processing time\n● Comprehensive sales reporting capabilities" 
      },
      unstructured_data: { 
        question: "Raw uploaded content", 
        answer: "" 
      }
    });
  };

  const generateStructuredSections = (content) => {
    // Clean up any YAML front matter
    let cleanContent = content.replace(/^---$\n([\s\S]*?)^---$\n/gm, '');
    
    // Debug: Log the original content length
    console.log('Original content length:', content.length);
    console.log('Clean content length:', cleanContent.length);
    
    // Replace any non-standard bullet points with markdown-style bullets
    cleanContent = cleanContent.replace(/^[●•◦]\s+/gm, '- ');
    
    // Ensure all headings have proper markdown format
    cleanContent = cleanContent.replace(/^(\d+\.)\s+([A-Z][^:\n]*):?$/gm, '## $1 $2');
    
    // Improve table formatting
    const tableRegex = /(\|[^\n]+\|\n)+((\|[-:| ]+\|\n)(\|[^\n]+\|\n)+)?/g;
    cleanContent = cleanContent.replace(tableRegex, (match) => {
      const rows = match.trim().split('\n');
      const processedRows = rows.map(row => {
        let processedRow = row;
        if (!processedRow.startsWith('|')) processedRow = '|' + processedRow;
        if (!processedRow.endsWith('|')) processedRow = processedRow + '|';
        
        const cells = processedRow.split('|').filter(cell => cell !== '');
        const paddedCells = cells.map(cell => cell.trim());
        
        return '| ' + paddedCells.join(' | ') + ' |';
      });
      
      if (!rows.some(row => row.match(/\|[-:| ]+\|/))) {
        const headerCells = processedRows[0].split('|').filter(cell => cell !== '');
        const separatorRow = '| ' + headerCells.map(() => '---').join(' | ') + ' |';
        processedRows.splice(1, 0, separatorRow);
      }
      
      return processedRows.join('\n') + '\n';
    });
    
    // Add document title if not present
    if (!cleanContent.startsWith('# Business Requirements Document')) {
      cleanContent = '# Business Requirements Document\n\n' + cleanContent;
    }
    
    // Add Document Control table if section 1 exists and doesn't have a table
    if (cleanContent.includes('## 1. Document Control') || cleanContent.includes('1. Document Control')) {
      // Check if there's already a table in the Document Control section
      const docControlSection = cleanContent.match(/(## 1\. Document Control|1\. Document Control)[\s\S]*?(?=## 2\.|2\.|$)/);
      if (docControlSection && !docControlSection[0].includes('| Version |')) {
        // Replace the Document Control section with one that includes the table
        const currentDate = new Date().toLocaleDateString();
        const docControlTable = `## 1. Document Control

| Version | Date | Author | Description |
|---------|------|--------|-------------|
| 1.0 | ${currentDate} | Generated | Initial Draft |

`;
        cleanContent = cleanContent.replace(/(## 1\. Document Control|1\. Document Control)[\s\S]*?(?=## 2\.|2\.|$)/, docControlTable);
      }
    }
    
    console.log('Final content length:', cleanContent.length);
    console.log('Content preview:', cleanContent.substring(0, 500));
    
    // Debug: Check for specific sections
    const hasScope = cleanContent.includes('4. Scope') || cleanContent.includes('## 4. Scope');
    const hasBusinessRequirements = cleanContent.includes('5. Business Requirements') || cleanContent.includes('## 5. Business Requirements');
    const hasDocumentControl = cleanContent.includes('1. Document Control') || cleanContent.includes('## 1. Document Control');
    console.log('Has Scope section:', hasScope);
    console.log('Has Business Requirements section:', hasBusinessRequirements);
    console.log('Has Document Control section:', hasDocumentControl);
    
    // Debug: Check if Document Control table has current date
    if (hasDocumentControl) {
      const hasCurrentDate = cleanContent.includes(new Date().toLocaleDateString());
      console.log('Document Control table has current date:', hasCurrentDate);
    }
    
    // Debug: Show content around section 4 and 5
    const scopeIndex = cleanContent.indexOf('4. Scope') !== -1 ? cleanContent.indexOf('4. Scope') : cleanContent.indexOf('## 4. Scope');
    const businessReqIndex = cleanContent.indexOf('5. Business Requirements') !== -1 ? cleanContent.indexOf('5. Business Requirements') : cleanContent.indexOf('## 5. Business Requirements');
    
    if (scopeIndex !== -1) {
      console.log('Scope section preview:', cleanContent.substring(scopeIndex, scopeIndex + 200));
    }
    if (businessReqIndex !== -1) {
      console.log('Business Requirements section preview:', cleanContent.substring(businessReqIndex, businessReqIndex + 200));
    }
    
    return cleanContent;
  };

  const handleDownload = async () => {
    console.log(result.substring(0,100))
    try {
      const response = await api.convertMarkdownToPdf(result);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'generated_Business_Requirements.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

 const downloadAsPDF = () => {
    // Get the raw markdown content directly
    if (!result) return;
    
    // Use the improved structured content
    const formattedContent = generateStructuredSections(result);
    
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });
    const title = "Business Requirements Document";
    
    // Add title
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, pdf.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
    
    // Reset font
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    
    let yPos = 35;
    const margin = 15;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);
    
    // Split content into sections by headings
    const sections = formattedContent.split(/(?=^#\s+)/m);
    
    // Improved table parsing function
    function parseMarkdownTable(lines, startIndex) {
        const tableLines = [];
        let currentIndex = startIndex;
        
        // Collect all table lines
        while (currentIndex < lines.length && lines[currentIndex].trim().startsWith('|')) {
            const line = lines[currentIndex].trim();
            if (line.match(/^\|[-:\s|]+\|$/)) {
                // Skip separator line but note its position
                currentIndex++;
                continue;
            }
            tableLines.push(line);
            currentIndex++;
        }
        
        if (tableLines.length === 0) return { tableData: [], endIndex: startIndex };
        
        // Parse each line into cells
        const tableData = tableLines.map(line => {
            return line.split('|')
                .slice(1, -1) // Remove empty first and last elements
                .map(cell => cell.trim());
        });
        
        return { tableData, endIndex: currentIndex };
    }
    
    // Improved table rendering function
    function renderTable(tableData) {
        if (tableData.length === 0) return;
        
        const minCellPadding = 3;
        const rowHeight = 7;
        const headerHeight = 10; // Increased header height to accommodate longer text
        
        // Calculate optimal column widths
        const numCols = tableData[0].length;
        const cellWidths = new Array(numCols).fill(0);
        
        // Calculate minimum width needed for each column
        tableData.forEach(row => {
            row.forEach((cell, colIndex) => {
                const cellText = cell.toString();
                const textWidth = pdf.getTextWidth(cellText);
                // Add extra padding for longer words and ensure minimum width
                const extraPadding = cellText.length > 8 ? 5 : 0;
                const minWidth = Math.max(textWidth + (minCellPadding * 2) + extraPadding, 30);
                cellWidths[colIndex] = Math.max(cellWidths[colIndex], minWidth);
            });
        });
        
        // Scale down if table is too wide
        const totalTableWidth = cellWidths.reduce((sum, width) => sum + width, 0);
        if (totalTableWidth > contentWidth) {
            const scaleFactor = contentWidth / totalTableWidth;
            cellWidths.forEach((width, index) => {
                cellWidths[index] = width * scaleFactor;
            });
        }
        
        // Calculate table height to check if we need a new page
        const estimatedHeight = (tableData.length * rowHeight) + headerHeight + 10;
        if (yPos + estimatedHeight > pdf.internal.pageSize.getHeight() - 20) {
            pdf.addPage();
            yPos = 20;
        }
        
        const tableStartY = yPos;
        const tableWidth = cellWidths.reduce((sum, width) => sum + width, 0);
        
        // Draw table background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin, tableStartY, tableWidth, estimatedHeight, 'F');
        
        // Set border style
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        
        let currentY = tableStartY;
        
        // Render each row
        tableData.forEach((row, rowIndex) => {
            const isHeader = rowIndex === 0;
            const currentRowHeight = isHeader ? headerHeight : rowHeight;
            
            // Draw header background
            if (isHeader) {
                pdf.setFillColor(240, 240, 240);
                pdf.rect(margin, currentY, tableWidth, currentRowHeight, 'F');
            }
            
            // Set font style
            pdf.setFont('helvetica', isHeader ? 'bold' : 'normal');
            pdf.setFontSize(isHeader ? 10 : 9);
            
            let currentX = margin;
            
            // Render each cell in the row
            row.forEach((cell, colIndex) => {
                const cellWidth = cellWidths[colIndex];
                const cellText = cell.toString();
                
                // Handle text wrapping - ensure adequate space for full text
                const maxCellWidth = cellWidth - (minCellPadding * 2);
                
                // For headers or important text, try to avoid wrapping if possible
                const shouldAvoidWrapping = isHeader && cellText.length <= 15;
                const wrappedText = shouldAvoidWrapping && pdf.getTextWidth(cellText) <= maxCellWidth
                    ? [cellText] 
                    : pdf.splitTextToSize(cellText, maxCellWidth);
                
                // Calculate text position (center vertically in cell)
                if (wrappedText.length === 1) {
                    // Single line - center vertically
                    const textY = currentY + (currentRowHeight / 2) + 2;
                    pdf.text(wrappedText[0], currentX + minCellPadding, textY);
                } else {
                    // Multi-line text - start from top with padding
                    const lineHeight = 4;
                    const startY = currentY + 4; // Start from top with small padding
                    wrappedText.forEach((line, lineIndex) => {
                        if (startY + (lineIndex * lineHeight) < currentY + currentRowHeight - 2) {
                            pdf.text(line, currentX + minCellPadding, startY + (lineIndex * lineHeight));
                        }
                    });
                }
                
                // Draw cell borders
                pdf.rect(currentX, currentY, cellWidth, currentRowHeight);
                
                currentX += cellWidth;
            });
            
            currentY += currentRowHeight;
        });
        
        // Draw outer table border
        pdf.setLineWidth(0.5);
        pdf.rect(margin, tableStartY, tableWidth, currentY - tableStartY);
        
        // Reset font
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        
        // Update yPos to after the table
        yPos = currentY + 10;
    }
    
    sections.forEach(section => {
        // Skip empty sections
        if (!section.trim()) return;
        
        const lines = section.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            
            // Check if we need a new page
            if (yPos > pdf.internal.pageSize.getHeight() - 20) {
                pdf.addPage();
                yPos = 20;
            }
            
            // Check if this is a heading
            if (line.startsWith('#')) {
                // Add space before heading if not at top of page
                if (yPos > 40) {
                    yPos += 5;
                }
                
                const level = line.match(/^#+/)[0].length;
                const text = line.replace(/^#+\s+/, '');
                
                // Set font size based on heading level
                const fontSize = Math.max(18 - ((level - 1) * 2), 12);
                pdf.setFontSize(fontSize);
                pdf.setFont('helvetica', 'bold');
                
                // Add heading
                pdf.text(text, margin, yPos);
                yPos += Math.max(fontSize * 0.6, 8);
                
                // Reset font
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'normal');
            } 
            // Check if this is the start of a table
            else if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
                const { tableData, endIndex } = parseMarkdownTable(lines, i);
                
                if (tableData.length > 0) {
                    renderTable(tableData);
                    i = endIndex - 1; // -1 because the loop will increment
                }
            }
            // Check if this is a list item
            else if (line.trim().startsWith('-') || line.trim().match(/^\d+\./)) {
                const isNumbered = line.trim().match(/^\d+\./);
                const itemText = isNumbered 
                    ? line.trim().replace(/^\d+\.\s*/, '')
                    : line.trim().substring(2);
                
                // Handle bold text in list items
                if (itemText.includes('**')) {
                    const parts = itemText.split(/(\*\*.*?\*\*)/g);
                    let xOffset = margin + 8;
                    
                    // Add bullet point
                    pdf.text(isNumbered ? '•' : '•', margin, yPos);
                    
                    parts.forEach(part => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            // Bold text
                            const boldText = part.substring(2, part.length - 2);
                            pdf.setFont('helvetica', 'bold');
                            pdf.text(boldText, xOffset, yPos);
                            xOffset += pdf.getTextWidth(boldText);
                            pdf.setFont('helvetica', 'normal');
                        } else if (part.trim()) {
                            // Regular text
                            pdf.text(part, xOffset, yPos);
                            xOffset += pdf.getTextWidth(part);
                        }
                    });
                    
                    yPos += 6;
                } else {
                    // Regular list item
                    const wrappedText = pdf.splitTextToSize(itemText, contentWidth - 15);
                    
                    pdf.text('•', margin, yPos);
                    pdf.text(wrappedText[0], margin + 8, yPos);
                    yPos += 6;
                    
                    // Handle additional wrapped lines
                    for (let j = 1; j < wrappedText.length; j++) {
                        pdf.text(wrappedText[j], margin + 8, yPos);
                        yPos += 6;
                    }
                }
            }
            // Regular text
            else if (line.trim()) {
                // Handle bold text in regular paragraphs
                if (line.includes('**')) {
                    const parts = line.split(/(\*\*.*?\*\*)/g);
                    let xOffset = margin;
                    let lineBreak = false;
                    
                    parts.forEach(part => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            // Bold text
                            const boldText = part.substring(2, part.length - 2);
                            pdf.setFont('helvetica', 'bold');
                            
                            // Check if text fits on current line
                            if (xOffset + pdf.getTextWidth(boldText) > pageWidth - margin) {
                                yPos += 6;
                                xOffset = margin;
                            }
                            
                            pdf.text(boldText, xOffset, yPos);
                            xOffset += pdf.getTextWidth(boldText);
                            pdf.setFont('helvetica', 'normal');
                        } else if (part.trim()) {
                            // Regular text - handle wrapping
                            const words = part.split(' ');
                            words.forEach(word => {
                                if (word.trim()) {
                                    const wordWidth = pdf.getTextWidth(word + ' ');
                                    if (xOffset + wordWidth > pageWidth - margin) {
                                        yPos += 6;
                                        xOffset = margin;
                                    }
                                    pdf.text(word + ' ', xOffset, yPos);
                                    xOffset += wordWidth;
                                }
                            });
                        }
                    });
                    
                    yPos += 6;
                } else {
                    // Regular paragraph with proper wrapping
                    const wrappedText = pdf.splitTextToSize(line, contentWidth);
                    
                    wrappedText.forEach(textLine => {
                        pdf.text(textLine, margin, yPos);
                        yPos += 6;
                    });
                }
            }
            // Empty line - add some space
            else {
                yPos += 4;
            }
        }
    });
    
    // Add page numbers
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 25, pdf.internal.pageSize.getHeight() - 10);
    }
    pdf.save('business_requirements.pdf');
};

  const downloadAsWord = () => {
  if (!result) return;

  // Use the improved structured content
  const formattedContent = generateStructuredSections(result);
  
  // Create a new Document
  const children = [];
  
  // Add title
  children.push(
    new Paragraph({
      text: "Business Requirements Document",
      heading: HeadingLevel.TITLE,
      spacing: { before: 200, after: 200 },
    })
  );
  
  // Split content into sections by headings
  const sections = formattedContent.split(/(?=^#\s+)/m);
  
  // Process each section
  sections.forEach(section => {
    // Skip empty sections
    if (!section.trim()) return;
    
    const lines = section.split('\n');
    
    // Track table state
    let tableRows = [];
    let inTable = false;
    let isHeaderRow = true;
    let columnCount = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Skip empty lines
      if (!line.trim()) continue;
      
      // IMPROVED: Better table separator detection
      // Skip table separator rows (|---|---|) and standalone dash lines
      if (line.trim().match(/^\|[\s\-\:\|]+\|$/) || line.trim().match(/^[\-\s]+$/)) {
        // This is a table separator row or standalone dashes - skip it
        if (inTable) {
          isHeaderRow = false; // Mark that we're past the header
        }
        continue;
      }
      
      // Check if this is a heading
      if (line.startsWith('#')) {
        // If we were in a table, finalize it
        if (inTable && tableRows.length > 0) {
          children.push(createTable(tableRows));
          tableRows = [];
          inTable = false;
          isHeaderRow = true;
          columnCount = 0;
        }
        
        const level = line.match(/^#+/)[0].length;
        const text = line.replace(/^#+\s+/, '');
        
        // Map markdown heading level to Word heading level
        const headingLevel = [
          HeadingLevel.HEADING_1,
          HeadingLevel.HEADING_2,
          HeadingLevel.HEADING_3,
          HeadingLevel.HEADING_4,
          HeadingLevel.HEADING_5,
          HeadingLevel.HEADING_6
        ][Math.min(level - 1, 5)];
        
        children.push(
          new Paragraph({
            text: text,
            heading: headingLevel,
            spacing: { before: 240, after: 120 },
          })
        );
      } 
      // Check if this is a table row
      else if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
        // Start collecting table data
        inTable = true;
        
        // Parse the cells
        const cells = line.split('|')
          .filter(cell => cell !== '')
          .map(cell => cell.trim());
        
        if (cells.length > 0) {
          // Set column count on first row
          if (columnCount === 0) {
            columnCount = cells.length;
          }
          
          // Create table cells
          const tableCells = cells.map(cell => {
            // Remove markdown formatting
            const cellText = cell.replace(/\*\*(.*?)\*\*/g, '$1');
            
            return new TableCell({
              children: [
                new Paragraph({
                  text: cellText,
                  spacing: { before: 50, after: 50 },
                })
              ],
              shading: isHeaderRow ? { fill: "F2F2F2" } : undefined,
            });
          });
          
          // Ensure all rows have the same number of cells
          while (tableCells.length < columnCount) {
            tableCells.push(new TableCell({
              children: [
                new Paragraph({
                  text: "",
                  spacing: { before: 50, after: 50 },
                })
              ]
            }));
          }
          
          // Add row to table
          tableRows.push(new TableRow({ 
            children: tableCells.slice(0, columnCount),
            tableHeader: isHeaderRow
          }));
          
          // After first data row, subsequent rows are not headers
          if (isHeaderRow && cells.some(cell => cell.trim())) {
            isHeaderRow = false;
          }
        }
      }
      // Check if this is a list item
      else if (line.trim().startsWith('-')) {
        // If we were in a table, finalize it
        if (inTable && tableRows.length > 0) {
          children.push(createTable(tableRows));
          tableRows = [];
          inTable = false;
          isHeaderRow = true;
          columnCount = 0;
        }
        
        // Handle bold text in list items
        const text = line.trim().substring(2).replace(/\*\*(.*?)\*\*/g, '$1');
        
        children.push(
          new Paragraph({
            text: text,
            bullet: { level: 0 },
            spacing: { before: 100, after: 100 },
          })
        );
      }
      // Regular text
      else if (line.trim()) {
        // If we were in a table, finalize it
        if (inTable && tableRows.length > 0) {
          children.push(createTable(tableRows));
          tableRows = [];
          inTable = false;
          isHeaderRow = true;
          columnCount = 0;
        }
        
        // Handle bold text in paragraphs
        const text = line.trim().replace(/\*\*(.*?)\*\*/g, '$1');
        
        children.push(
          new Paragraph({
            text: text,
            spacing: { before: 100, after: 100 },
          })
        );
      }
    }
    
    // If we were in a table at the end, finalize it
    if (inTable && tableRows.length > 0) {
      children.push(createTable(tableRows));
    }
  });
  
  const doc = new Document({
    sections: [{
      properties: {},
      children: children,
    }],
  });

  window.debugWordConversion = debugWordConversion;

  Packer.toBlob(doc).then(blob => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = url;
    a.download = 'business_requirements.docx';
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  });
};

// Helper function to create tables with consistent styling
const createTable = (tableRows) => {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1 },
      bottom: { style: BorderStyle.SINGLE, size: 1 },
      left: { style: BorderStyle.SINGLE, size: 1 },
      right: { style: BorderStyle.SINGLE, size: 1 },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
      insideVertical: { style: BorderStyle.SINGLE, size: 1 },
    },
    rows: tableRows,
  });
};

// DEBUGGING: Add this function to see what lines are being processed
const debugWordConversion = () => {
  if (!result) {
    console.log('No result to debug');
    return;
  }
  
  const formattedContent = generateStructuredSections(result);
  const lines = formattedContent.split('\n');
  
  console.log('=== WORD CONVERSION DEBUG ===');
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (trimmed.includes('|') || trimmed.match(/^[\-\s]+$/)) {
      console.log(`Line ${index}: "${line}"`);
      console.log(`  - Is table separator: ${trimmed.match(/^\|[\s\-\:\|]+\|$/) ? 'YES (SKIP)' : 'NO'}`);
      console.log(`  - Is dash line: ${trimmed.match(/^[\-\s]+$/) ? 'YES (SKIP)' : 'NO'}`);
      console.log(`  - Is table row: ${trimmed.startsWith('|') && trimmed.endsWith('|') && !trimmed.match(/^\|[\s\-\:\|]+\|$/) ? 'YES (INCLUDE)' : 'NO'}`);
    }
  });
  console.log('=== END DEBUG ===');
};
const copyToClipboard = async (text) => {
  // Check if we're on HTTPS and clipboard API is available
  if (window.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Clipboard API failed:', err);
    }
  }
  
  // Fallback for HTTP or when clipboard API fails
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    document.body.removeChild(textArea);
    console.error('Copy failed:', err);
    return false;
  }
};
// Add this to your component for debugging


  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-textPrimary">Business Requirements Generator</h1>
        </div>
        
        {/* Tab Navigation */}
        <div className="mt-8 border-b border-borderLight">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('input')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'input'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-textSecondary hover:text-textPrimary hover:border-inputBorder'
              }`}
            >
              Input & Upload
            </button>
            <button
              onClick={() => setActiveTab('results')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'results'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-textSecondary hover:text-textPrimary hover:border-inputBorder'
              }`}
            >
              Results
              {result && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[rgba(16,185,129,0.15)] text-[#047857]">
                  Ready
                </span>
              )}
            </button>
          </nav>
        </div>
        
        {/* Input Tab */}
        {activeTab === 'input' && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Side - File Upload */}
            <div className="col-span-1">
              <div className="bg-white p-6 rounded-lg shadow-md h-full">
                <h2 className="text-xl font-bold text-textPrimary mb-2">Upload Files</h2>
                <p className="text-sm text-textSecondary mb-4">Drag and drop files or click to browse</p>
                
                <div 
                  className="border-2 border-dashed border-inputBorder rounded-lg p-8 text-center hover:border-accent transition-colors duration-200 cursor-pointer"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-accent', 'bg-[rgba(8,145,178,0.08)]');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-accent', 'bg-[rgba(8,145,178,0.08)]');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-accent', 'bg-[rgba(8,145,178,0.08)]');
                    const files = Array.from(e.dataTransfer.files);
                    handleFileUpload(files);
                  }}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <div className="mb-4">
                    <svg 
                      className="mx-auto h-12 w-12 text-textSecondary" 
                      stroke="currentColor" 
                      fill="none" 
                      viewBox="0 0 48 48" 
                      aria-hidden="true"
                    >
                      <path 
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" 
                        strokeWidth={2} 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                      />
                    </svg>
                  </div>
                  
                  <p className="text-textSecondary mb-2">Drop files here or click to upload</p>
                  <p className="text-xs text-textSecondary mb-4">Support for images, videos, documents and more</p>
                  
                  <button 
                    type="button"
                    className="text-white px-3 py-1.5 rounded-md transition-colors duration-200 text-xs font-medium"
                    style={{ background: '#093878' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById('fileInput').click();
                    }}
                  >
                    Browse Files
                  </button>
                  
                  <input
                    id="fileInput"
                    type="file"
                    multiple
                    accept=".doc,.docx,.txt,.pdf,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov"
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      handleFileUpload(files);
                    }}
                  />
                </div>
                
                {/* File List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-textPrimary mb-2">Uploaded Files:</h3>
                    <div className="space-y-2">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-panelHover rounded">
                          <div className="flex items-center space-x-2">
                            <svg className="h-4 w-4 text-textSecondary" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-textPrimary">{file.name}</span>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-semanticFail hover:text-red-700 p-1"
                          >
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Raw Uploaded Content Field */}
                {(uploadedFiles.length > 0 || stakeholderInputs.unstructured_data.answer) && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-textPrimary mb-2">Raw Uploaded Content:</h3>
                    <div className="relative">
                      <textarea
                        className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none text-sm"
                        rows="6"
                        value={stakeholderInputs.unstructured_data.answer}
                        onChange={(e) => handleInputChange('unstructured_data', e.target.value)}
                        placeholder="Content extracted from uploaded files will appear here. You can also paste or type content manually..."
                        readOnly={false}
                      />
                    </div>
                    <p className="text-xs text-textSecondary mt-1">
                      Content extracted from uploaded files. You can edit this text if needed.
                    </p>
                    
                    <button
                       type="button"
                       className="flex-1 mt-6 text-white py-1.5 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                       style={{ background: '#093878' }}
                       disabled={loading || !stakeholderInputs.unstructured_data.answer.trim()}
                       onClick={handleSubmit}
                     >
                       {loading ? 'Generating...' : 'Generate Business Requirements'}
                     </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="col-span-1">
              <div className="bg-white p-6 rounded-lg shadow-lg h-full">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-textPrimary">
                    Business Requirements Form
                    {uploadedFiles.length > 0 && (
                      <span className="text-sm text-textSecondary ml-2">(Disabled - File uploaded)</span>
                    )}
                  </h3>
                  
                  <button
                    type="button"
                    onClick={loadStandardInputs}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    Load Standard Inputs
                  </button>
                </div>
                
                <div className={`${uploadedFiles.length > 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                  
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Step 1: First 4 questions */}
                    {currentStep === 1 && (
                      <>
                        <div>
                          <label htmlFor="business_context" className="block text-sm font-medium text-textPrimary mb-2">
                            What is the business context or domain?
                          </label>
                          <textarea
                            id="business_context"
                            className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                            rows="3"
                            value={stakeholderInputs.business_context.answer}
                            onChange={(e) => handleInputChange('business_context', e.target.value)}
                            placeholder="Describe the business context or domain..."
                          />
                        </div>

                        <div>
                          <label htmlFor="primary_goal" className="block text-sm font-medium text-textPrimary mb-2">
                            What is the primary goal of this project?
                          </label>
                          <textarea
                            id="primary_goal"
                            className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                            rows="3"
                            value={stakeholderInputs.primary_goal.answer}
                            onChange={(e) => handleInputChange('primary_goal', e.target.value)}
                            placeholder="Describe the primary goal..."
                          />
                        </div>

                        <div>
                          <label htmlFor="key_stakeholders" className="block text-sm font-medium text-textPrimary mb-2">
                            Who are the key stakeholders?
                          </label>
                          <textarea
                            id="key_stakeholders"
                            className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                            rows="3"
                            value={stakeholderInputs.key_stakeholders.answer}
                            onChange={(e) => handleInputChange('key_stakeholders', e.target.value)}
                            placeholder="List the key stakeholders..."
                          />
                        </div>

                        <div>
                          <label htmlFor="target_users" className="block text-sm font-medium text-textPrimary mb-2">
                            Who are the target users?
                          </label>
                          <textarea
                            id="target_users"
                            className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                            rows="3"
                            value={stakeholderInputs.target_users.answer}
                            onChange={(e) => handleInputChange('target_users', e.target.value)}
                            placeholder="Describe the target users..."
                          />
                        </div>

                                                 <div className="pt-4">
                           <button
                             type="button"
                             onClick={handleNext}
                             className="w-24 text-white py-1.5 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-colors text-sm"
                             style={{ background: '#093878' }}
                           >
                             Next
                           </button>
                         </div>
                      </>
                    )}

                    {/* Step 2: Remaining 4 questions */}
                    {currentStep === 2 && (
                      <>
                        <div>
                          <label htmlFor="key_features" className="block text-sm font-medium text-textPrimary mb-2">
                            What are the key features needed?
                          </label>
                          <textarea
                            id="key_features"
                            className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                            rows="3"
                            value={stakeholderInputs.key_features.answer}
                            onChange={(e) => handleInputChange('key_features', e.target.value)}
                            placeholder="List the key features..."
                          />
                        </div>

                        <div>
                          <label htmlFor="data_sources" className="block text-sm font-medium text-textPrimary mb-2">
                            What data sources will be used?
                          </label>
                          <textarea
                            id="data_sources"
                            className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                            rows="3"
                            value={stakeholderInputs.data_sources.answer}
                            onChange={(e) => handleInputChange('data_sources', e.target.value)}
                            placeholder="Describe the data sources..."
                          />
                        </div>

                        <div>
                          <label htmlFor="constraints" className="block text-sm font-medium text-textPrimary mb-2">
                            Are there any constraints or limitations?
                          </label>
                          <textarea
                            id="constraints"
                            className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                            rows="3"
                            value={stakeholderInputs.constraints.answer}
                            onChange={(e) => handleInputChange('constraints', e.target.value)}
                            placeholder="Describe any constraints..."
                          />
                        </div>

                        <div>
                          <label htmlFor="success_criteria" className="block text-sm font-medium text-textPrimary mb-2">
                            What are the success criteria?
                          </label>
                          <textarea
                            id="success_criteria"
                            className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                            rows="3"
                            value={stakeholderInputs.success_criteria.answer}
                            onChange={(e) => handleInputChange('success_criteria', e.target.value)}
                            placeholder="Describe the success criteria..."
                          />
                        </div>

                        <div className="flex space-x-4 pt-4">
                           <button
                             type="button"
                             onClick={handlePrevious}
                             className="w-24 bg-textSecondary hover:bg-textPrimary text-white py-1.5 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-colors text-sm"
                           >
                             Previous
                           </button>
                           <button
                             type="submit"
                             className="w-24 flex-1 text-white py-1.5 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-colors text-sm"
                             style={{ background: '#093878' }}
                             disabled={loading}
                           >
                             {loading ? 'Generating...' : 'Generate Business Requirements'}
                           </button>
                         </div>
                      </>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Results Tab */}
        {activeTab === 'results' && (
          <div className="mt-8">
            {loading ? (
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
                </div>
              </div>
            ) : result ? (
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h2 className="text-xl font-bold text-textPrimary mb-4">Generated Business Requirements</h2>
                <div 
                  className="prose max-w-none bg-white p-6 rounded-lg shadow-sm border border-borderLight mb-4 overflow-y-auto markdown-content"
                  style={{ maxHeight: '70vh' }}
                >
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({node, ...props}) => <p className="business-requirements-markdown-p" {...props} />,
                      h1: ({node, children, ...props}) => children && children.length > 0 ? <h1 className="business-requirements-markdown-h1" {...props}>{children}</h1> : null,
                      h2: ({node, children, ...props}) => children && children.length > 0 ? <h2 className="business-requirements-markdown-h2" {...props}>{children}</h2> : null,
                      h3: ({node, children, ...props}) => children && children.length > 0 ? <h3 className="business-requirements-markdown-h3" {...props}>{children}</h3> : null,
                      h4: ({node, children, ...props}) => children && children.length > 0 ? <h4 className="business-requirements-markdown-h4" {...props}>{children}</h4> : null,
                      ul: ({node, ...props}) => <ul className="business-requirements-markdown-ul" {...props} />,
                      ol: ({node, ...props}) => <ol className="business-requirements-markdown-ol" {...props} />,
                      li: ({node, ...props}) => <li className="business-requirements-markdown-li" {...props} />,
                      table: ({node, ...props}) => <table className="business-requirements-markdown-table" {...props} />,
                      thead: ({node, ...props}) => <thead className="business-requirements-markdown-thead" {...props} />,
                      tbody: ({node, ...props}) => <tbody className="business-requirements-markdown-tbody" {...props} />,
                      tr: ({node, ...props}) => <tr className="business-requirements-markdown-tr" {...props} />,
                      th: ({node, ...props}) => <th className="business-requirements-markdown-th" {...props} />,
                      td: ({node, ...props}) => <td className="business-requirements-markdown-td" {...props} />,
                      blockquote: ({node, ...props}) => <blockquote className="business-requirements-markdown-blockquote" {...props} />,
                      code: ({node, ...props}) => <code className="business-requirements-markdown-code" {...props} />
                    }}
                  >
                    {result}
                  </ReactMarkdown>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => {
                      const structuredContent = generateStructuredSections(result);
                      const element = document.createElement("a");
                      const file = new Blob([structuredContent], {type: 'text/plain'});
                      element.href = URL.createObjectURL(file);
                      element.download = "business_requirements.txt";
                      document.body.appendChild(element);
                      element.click();
                      document.body.removeChild(element);
                    }}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    Download as Text
                  </button>
                  
                  <button
                    onClick={handleDownload}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    Download as PDF
                  </button>
                  
                  <button
                    onClick={downloadAsWord}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    Download as Word
                  </button>
                  
                  <button
                    onClick={() => {
                      const structuredContent = generateStructuredSections(result);
                      copyToClipboard(structuredContent);
                      toast.success("Business requirements copied to clipboard!");
                    }}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    Copy to Clipboard
                  </button>
                  <button
                    onClick={() =>{
                      sessionStorage.setItem('requirements', result || '');
                      navigate('/data-model-generator');
                    }}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    Generate Data Model
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h2 className="text-xl font-bold text-textPrimary mb-4">Generated Business Requirements</h2>
                <p className="text-textSecondary italic">
                  Fill out the stakeholder inputs and click "Generate Business Requirements" to see the results here.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{ fontSize: '14px', borderRadius: '8px' }}
      />
    </div>
  );
};

export default BusinessRequirementGenerator; 